'use client';
import React, { useState } from 'react';
import { Control, useController } from 'react-hook-form';
import { ImageUpload } from '@/components/ui/image-upload';
import { X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/trpc/react';

interface UploadClothingStepProps {
  themeId: string;
  gender?: 'male' | 'female';
  control: Control<any>;
  setValue: any;
}

type GarmentType = 'top' | 'bottom' | 'one-piece';

interface GarmentRecord {
  url: string;
  type: GarmentType;
}

type GarmentStatus = 'validating' | 'valid' | 'invalid';

interface GarmentImageProps {
  url: string;
  index: number;
  onRemove: () => void;
  status?: GarmentStatus;
  onValidationComplete?: (isValid: boolean, url: string) => void;
}

function GarmentImage({ url, index, onRemove, status = 'valid', onValidationComplete }: GarmentImageProps) {
  const validateGarment = api.fal.validateGarmentImage.useMutation({
    onSuccess: result => {
      if (onValidationComplete) {
        onValidationComplete(result.isValid, url);
      }
    }
  });

  // Start validation if in validating state
  React.useEffect(() => {
    if (status === 'validating') {
      validateGarment.mutate({ imageUrl: url });
    }
  }, [url, status, validateGarment]);

  const statusIndicator = {
    validating: validateGarment.isPending ? (
      <>
        <Loader2 className="w-3 h-3 animate-spin" />
        <span className="text-sm text-muted-foreground">Validating...</span>
      </>
    ) : validateGarment.data?.isValid ? (
      <>
        <div className="w-3 h-3 rounded-full bg-green-500" />
        <span className="text-sm text-muted-foreground">Valid</span>
      </>
    ) : (
      <>
        <div className="w-3 h-3 rounded-full bg-red-500" />
        <span className="text-sm text-muted-foreground">Invalid</span>
      </>
    ),
    valid: (
      <>
        <div className="w-2 h-2 rounded-full bg-green-500 mt-[1px]" title="Valid" />
        <span className="text-sm text-muted-foreground">Valid</span>
      </>
    ),
    invalid: (
      <>
        <div className="w-3 h-3 rounded-full bg-red-500" />
        <span className="text-sm text-muted-foreground">Invalid</span>
      </>
    )
  };

  return (
    <div className="flex items-center space-x-4 p-2 rounded-lg border border-border bg-card">
      <div className="h-16 w-16 rounded-md overflow-hidden flex-shrink-0">
        <img src={url} alt={`Outfit ${index + 1}`} className="w-full h-full object-cover" />
      </div>
      <div className="flex flex-col items-start space-y-2">
        <span className="text-lg font-medium min-w-[100px]">Outfit {index + 1}</span>
        <div className="flex items-center space-x-2">{statusIndicator[status]}</div>
      </div>
      <div className="flex-grow" />
      <Button variant="ghost" size="icon" onClick={onRemove} className="text-muted-foreground hover:text-foreground">
        <X className="w-4 h-4" />
      </Button>
    </div>
  );
}

export function UploadClothingStep({ setValue, control }: UploadClothingStepProps) {
  const { field } = useController({
    name: 'garmentImages',
    control,
    defaultValue: []
  });

  const [validatedGarments, setValidatedGarments] = useState<GarmentRecord[]>(field.value || []);
  const [validatingImages, setValidatingImages] = useState<string[]>([]);
  const [invalidImages, setInvalidImages] = useState<string[]>([]);

  const handleImageUploaded = (url: string) => {
    setValidatingImages(prev => [...prev, url]);
  };

  const handleValidationComplete = (isValid: boolean, url: string) => {
    if (isValid) {
      const newGarment: GarmentRecord = { url, type: 'one-piece' };
      const newGarments = [...validatedGarments, newGarment];
      setValidatedGarments(newGarments);
      setValue('garmentImages', newGarments);
    } else {
      setInvalidImages(prev => [...prev, url]);
    }
    setValidatingImages(prev => prev.filter(img => img !== url));
  };

  const handleImageRemoved = (urlToRemove: string) => {
    setValidatedGarments(prev => prev.filter(g => g.url !== urlToRemove));
    setValidatingImages(prev => prev.filter(url => url !== urlToRemove));
    setInvalidImages(prev => prev.filter(url => url !== urlToRemove));
    setValue(
      'garmentImages',
      validatedGarments.filter(g => g.url !== urlToRemove)
    );
  };

  return (
    <div className="flex flex-col space-y-6 h-full w-full">
      <h2 className="text-2xl font-semibold">Upload clothing</h2>

      <div className="flex flex-col space-y-4">
        {validatedGarments.map((garment, index) => (
          <GarmentImage
            key={garment.url}
            url={garment.url}
            index={index}
            status="valid"
            onRemove={() => handleImageRemoved(garment.url)}
          />
        ))}

        {validatingImages.map((url, index) => (
          <GarmentImage
            key={url}
            url={url}
            index={validatedGarments.length + index}
            status="validating"
            onValidationComplete={handleValidationComplete}
            onRemove={() => handleImageRemoved(url)}
          />
        ))}

        {invalidImages.map((url, index) => (
          <GarmentImage
            key={url}
            url={url}
            index={validatedGarments.length + validatingImages.length + index}
            status="invalid"
            onRemove={() => handleImageRemoved(url)}
          />
        ))}

        <div className="w-full">
          <ImageUpload
            onImageUploaded={handleImageUploaded}
            className="w-full"
            buttonLabel="Add Clothing"
            showPreview={false}
          />
        </div>
      </div>
    </div>
  );
}
